import { Button, ButtonLink, TextBlock, HighlightKeyword, Icon } from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import { getLastDateOfPreviousMonth } from 'date-functions';
import React, { Fragment, PureComponent } from 'react';
import styled from 'styled-components';
import { ClientIdContext } from 'bk-web-app';

import type { DataEntry, DateRange, LedgerAccount } from './types';
import FloatingExpandableText from './FloatingExpandableText';
import formattedCentsAmount from './formattedCentsAmount';
import ledgerAccountCategoryTypes from './ledgerAccountCategoryTypes';
import LedgerAccountCSVExportContainer from './LedgerAccountCSVExportContainer';
import RefreshBalanceButtonContainer from './RefreshBalanceButtonContainer';
import RefreshGLBalanceButtonContainer from './RefreshGLBalanceButtonContainer';
import FeatureFlagContainer from '../client-web-app/components/sidebar/FeatureFlagContainer';

// Inherited from TextBlock
const LINE_HEIGHT = 16;

const PADDING_TOP_HEIGHT = 24;

const PADDING_BOTTOM_HEIGHT = 16;

const ROW_GAP = 12;

const isBalanceSheet = ledgerAccount => {
  const ledgerAccountCategoryType =
    ledgerAccount.category && ledgerAccount.category.ledgerAccountCategoryType;
  return ledgerAccountCategoryType === ledgerAccountCategoryTypes.BALANCE_SHEET;
};

const formatOptionalCurrencyAmountForDisplay = amountInCents =>
  typeof amountInCents === 'number' ? formattedCentsAmount(amountInCents) : '—';

const LIABILITY_ID = '3';
const EQUITY_ID = '5';
const REVENUE_ID = '6';

const formatForDisplay = (amount?: number, ledgerCategoryId?: string): string => {
  if (
    amount &&
    ledgerCategoryId &&
    [LIABILITY_ID, REVENUE_ID, EQUITY_ID].includes(ledgerCategoryId)
  ) {
    if (amount === 0) return formatOptionalCurrencyAmountForDisplay(amount);
    return formatOptionalCurrencyAmountForDisplay(amount * -1);
  }
  return formatOptionalCurrencyAmountForDisplay(amount);
};

const LedgerAccountHeaderLayout = styled.div`
  display: grid;
  grid-column-gap: ${ROW_GAP}px;
  grid-template-columns: auto max-content;
  margin: 0 32px;
  padding: ${PADDING_TOP_HEIGHT}px 0 ${PADDING_BOTTOM_HEIGHT}px;
`;

const LedgerDescriptionLayout = styled.div`
  display: grid;
`;

const LedgerNameLayout = styled.div`
  align-items: flex-start;
  display: grid;
  grid-template-columns: max-content max-content;
  grid-column-gap: 8px;
`;

const LedgerNameButton = styled(ButtonLink)`
  color: ${colors.charcoal};
  font-weight: bold;
`;

const NoteText = styled(TextBlock).attrs(() => ({ color: colors.smoke, styleType: 'small' }))`
  max-width: 800px;
`;

const BalancesLayout = styled.div`
  display: grid;
  grid-column-gap: 8px;
  grid-row-gap: ${ROW_GAP}px;
  grid-template-columns: max-content max-content max-content;
  grid-template-rows: ${LINE_HEIGHT}px;
`;

const DownloadIcon = styled(Icon)`
  display: block;
`;

type LedgerAccountHeaderProps = {
  dateRange: DateRange;
  ledgerAccount: LedgerAccount;
  search?: string;
  shownDataEntries: Array<DataEntry>;
  toggleLedgerAccount: (ledger: LedgerAccount) => any;
};

class LedgerAccountHeader extends PureComponent<LedgerAccountHeaderProps> {
  static defaultProps = {
    search: undefined,
  };

  static getHeight = ({ ledgerAccount }: LedgerAccountHeaderProps) => {
    // For a balance sheet ledger, there is an opening and closing balance row in the summary column
    const summaryColumnHeight = isBalanceSheet(ledgerAccount)
      ? 2 * LINE_HEIGHT + ROW_GAP
      : LINE_HEIGHT;
    const { details, internalNotes } = ledgerAccount;
    // There's always the ledger name
    let numLines = 1;
    if (details) {
      numLines++;
    }
    if (internalNotes.length > 0) {
      numLines += internalNotes.length;
    }
    const linesHeight = numLines * LINE_HEIGHT;
    const gapsHeight = (numLines - 1) * ROW_GAP;
    const descriptionColumnHeight = linesHeight + gapsHeight;
    const tallestColumnHeight = Math.max(descriptionColumnHeight, summaryColumnHeight);
    return tallestColumnHeight + PADDING_TOP_HEIGHT + PADDING_BOTTOM_HEIGHT;
  };

  render() {
    const { dateRange, ledgerAccount, search, shownDataEntries, toggleLedgerAccount } = this.props;
    const { details, internalNotes } = ledgerAccount;
    const haveDetails = !!details;
    const haveNotes = internalNotes.length > 0;
    const haveComments = haveDetails || haveNotes;
    const ledgerAccountCategoryType =
      ledgerAccount.category && ledgerAccount.category.ledgerAccountCategoryType;
    return (
      <LedgerAccountHeaderLayout>
        <LedgerDescriptionLayout>
          <LedgerNameLayout>
            <LedgerNameButton
              data-bypass-dismiss-sidebar
              onMouseDown={() => toggleLedgerAccount(ledgerAccount)}
            >
              {ledgerAccount.name}
            </LedgerNameButton>
            <LedgerAccountCSVExportContainer
              dateRange={dateRange}
              dataEntriesToExport={shownDataEntries}
              ledgerAccount={ledgerAccount}
            >
              <DownloadIcon icon="downloadSquare" color="#098B8C" size="small" />
            </LedgerAccountCSVExportContainer>
            {haveComments && (
              <div>
                <Button
                  data-bypass-dismiss-sidebar
                  icon="notebook"
                  onMouseDown={() => toggleLedgerAccount(ledgerAccount)}
                  size="xsmall"
                  styleType="total-ghost"
                  title="This ledger has a description or internal notes"
                />
              </div>
            )}
          </LedgerNameLayout>
          {haveDetails && (
            <NoteText>
              <FloatingExpandableText>
                <HighlightKeyword keyword={search}>{details}</HighlightKeyword>
              </FloatingExpandableText>
            </NoteText>
          )}
          {haveNotes &&
            internalNotes.map(({ body, id }) => (
              <NoteText key={id}>
                <FloatingExpandableText>
                  Internal Note: <HighlightKeyword keyword={search}>{body}</HighlightKeyword>
                </FloatingExpandableText>
              </NoteText>
            ))}
        </LedgerDescriptionLayout>
        <ClientIdContext.Consumer>
          {clientId => (
            <FeatureFlagContainer clientId={clientId} feature="lightningGL">
              {isLightningGLEnabled => (
                <BalancesLayout>
                  {isBalanceSheet(ledgerAccount) ? (
                    <Fragment>
                      <TextBlock styleType="small">Opening Balance:</TextBlock>
                      <TextBlock align="right" styleType="small">
                        {isLightningGLEnabled
                          ? formatForDisplay(
                              ledgerAccount.openingBalanceInCents,
                              ledgerAccount.category?.id,
                            )
                          : formatOptionalCurrencyAmountForDisplay(
                              ledgerAccount.openingBalanceInCents,
                            )}
                      </TextBlock>
                      {isLightningGLEnabled ? (
                        <RefreshGLBalanceButtonContainer
                          dateRange={dateRange}
                          clientId={clientId}
                        />
                      ) : (
                        <RefreshBalanceButtonContainer
                          date={
                            dateRange.startDate && getLastDateOfPreviousMonth(dateRange.startDate)
                          }
                          ledgerAccountCategoryType={ledgerAccountCategoryType}
                        />
                      )}
                      <TextBlock styleType="small">Closing Balance:</TextBlock>
                      <TextBlock align="right" styleType="small">
                        {isLightningGLEnabled
                          ? formatForDisplay(
                              ledgerAccount.closingBalanceInCents,
                              ledgerAccount.category?.id,
                            )
                          : formatOptionalCurrencyAmountForDisplay(
                              ledgerAccount.closingBalanceInCents,
                            )}
                      </TextBlock>
                      {isLightningGLEnabled ? (
                        <RefreshGLBalanceButtonContainer
                          dateRange={dateRange}
                          clientId={clientId}
                        />
                      ) : (
                        <RefreshBalanceButtonContainer
                          date={dateRange.endDate}
                          ledgerAccountCategoryType={ledgerAccountCategoryType}
                        />
                      )}
                    </Fragment>
                  ) : (
                    <Fragment>
                      <TextBlock styleType="small">Total:</TextBlock>
                      <TextBlock align="right" styleType="small">
                        {isLightningGLEnabled
                          ? formatForDisplay(
                              ledgerAccount.sumTotalInCents,
                              ledgerAccount.category?.id,
                            )
                          : formatOptionalCurrencyAmountForDisplay(ledgerAccount.sumTotalInCents)}
                      </TextBlock>
                      {isLightningGLEnabled ? (
                        <RefreshGLBalanceButtonContainer
                          dateRange={dateRange}
                          clientId={clientId}
                        />
                      ) : (
                        <RefreshBalanceButtonContainer
                          dateRange={dateRange}
                          ledgerAccountCategoryType={ledgerAccountCategoryType}
                        />
                      )}
                    </Fragment>
                  )}
                </BalancesLayout>
              )}
            </FeatureFlagContainer>
          )}
        </ClientIdContext.Consumer>
      </LedgerAccountHeaderLayout>
    );
  }
}

export default LedgerAccountHeader;
